import React, {FC, ReactNode, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {usePrevious} from '../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {useEventHandler} from '../../../../../libs/shared/core/src/helpers/react/hooks/useEventHandler';
import {assertUnreachable} from '../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {DataEventActionsEnum} from '../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {OnboardingDataEventObject} from '../../../../../libs/shared/core/src/models/tracking/objectNames/onboardingDataEventObject';
import {useDataEventTracking} from '../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {AppPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {ChromelessButton} from '../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {InfoBubble} from '../../../../../libs/shared/ui/src/components/tip/infoBubble';
import {InfoBubbleContent} from '../../../../../libs/shared/ui/src/components/tip/infoBubbleContent';
import {InfoBubbleDescription} from '../../../../../libs/shared/ui/src/components/tip/infoBubbleContentDescription';
import {
  InfoBubblePill,
  InfoBubblePillVariantsEnum,
} from '../../../../../libs/shared/ui/src/components/tip/infoBubblePill';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {useAppDispatch, useAppSelector} from '../../../../helpers/appReduxHelpers';
import {getIsCustomer} from '../../../../store/data/company/companySelectors';
import {saveUserPreferencesAction} from '../../../../store/data/preferences/preferencesActions';
import {getActivationPreferences} from '../../../../store/data/preferences/preferencesSelectors';
import {OnboardingGuidesEnum} from '../../../onboarding/guide/onboardingGuideTypes';
import {AdminSetupGuidePopover} from '../../../onboarding/setupGuide/adminSetupGuidePopover';
import {DismissSetupGuideDialog} from '../../../onboarding/setupGuide/dismissSetupGuideDialog';
import {SetupGuideTopBarButton} from '../../../onboarding/setupGuide/setupGuideTopBarButton';
import {
  AdminSetupGuideConcreteScreen,
  AdminSetupGuideScreensEnum,
} from '../../../onboarding/setupGuide/setupGuideTypes';
import {
  connectChannelTask,
  inviteTeammatesTask,
  sharedWorkspaceTask,
} from '../../../onboarding/tasks/adminOnboardingTasks';
import {useAdminOnboardingTasks, useQueryLmsCourses} from '../../../onboarding/tasks/onboardingTaskHelpers';
import {
  AdminSetupGuideTasksAndProgress,
  SetupGuideScreenTask,
} from '../../../onboarding/tasks/onboardingTypes';
import {ContinueSetupTipsEnum, useOnboardingModalState} from './onboardingContext';
import {useSuspendedUntil} from './onboardingHelpers';

/*
 * Props.
 */

interface AdminSetupGuideProps {
  className?: string;
  panelId: AppPanelId;
}

/*
 * Style.
 */

const StyledInfoBubble = styled(InfoBubble)`
  width: 380px;
`;

const StyledInfoBubbleTitleDiv = styled.div`
  margin-bottom: 4px;
  font-weight: ${fontWeights.bold};
  font-size: ${fontSizes.veryLarge};
  line-height: 1.5;
  color: ${(p) => p.theme.greys.shade90};
`;

const StyledCloseButton = styled(ChromelessButton)`
  position: absolute;
  right: 7px;
  top: 7px;
  display: flex;
  width: 26px;
  height: 26px;
  padding: 5px;
  justify-content: center;
  align-items: center;
`;

const StyledCloseIcon = styled(Icon)`
  width: 16px;
  height: 16px;
  color: ${(p) => p.theme.greys.shade70};
`;

/*
 * Component.
 */

export const AdminSetupGuide: FC<AdminSetupGuideProps> = (props) => {
  const {panelId} = props;
  const isCustomer = useAppSelector(getIsCustomer);
  const {trackDataEvent} = useDataEventTracking();
  const dispatch = useAppDispatch();
  const tipAnchor = useRef<HTMLDivElement>(null);

  const {completedCourses, refetchCompletedCourses} = useQueryLmsCourses();
  const tasks = useAdminOnboardingTasks(completedCourses);
  const previousTasks = usePrevious(tasks);

  const {
    onboardingGuide,
    renderOnboardingGuideTransition,
    defaultAdminSetupGuideScreen,
    openAdminSetupGuideSetupScreen,
    openAdminSetupGuide,
    closeAdminSetupGuide,
    requestContinueSetupTip,
    requestedContinueSetupTip,
  } = useOnboardingModalState();

  const isAdminSetupGuideOpen = onboardingGuide === OnboardingGuidesEnum.ADMIN_SETUP;

  const tasksSuspendedUntilOpen = useSuspendedUntil(tasks, isAdminSetupGuideOpen, 500);

  const [isDismissPermanentlyDialogOpen, setIsDismissPermanentlyDialogOpen] = useState(false);

  const activationPreferences = useAppSelector(getActivationPreferences);

  const onAdminSetupGuideOpen = useEventHandler(() => {
    refetchCompletedCourses();
  });

  useEffect(() => {
    if (isAdminSetupGuideOpen) {
      onAdminSetupGuideOpen();
    }
  }, [isAdminSetupGuideOpen, onAdminSetupGuideOpen]);

  const [defaultScreen, setDefaultScreen] = useState<AdminSetupGuideConcreteScreen>(
    computeDefaultScreen(tasks, defaultAdminSetupGuideScreen),
  );

  useEffect(() => {
    setDefaultScreen(computeDefaultScreen(tasks, defaultAdminSetupGuideScreen));
  }, [isAdminSetupGuideOpen]);

  const {visibleContinueSetupTip, setPendingContinueSetupTip, clearRequestedContinueSetupTip} =
    useContinueSetupTip(previousTasks, tasks, isAdminSetupGuideOpen);

  const onContinueSetupTipDismiss = () => {
    setPendingContinueSetupTip(undefined);
    clearRequestedContinueSetupTip();

    if (requestedContinueSetupTip === ContinueSetupTipsEnum.KNOWS_SETUP_GUIDE_EXISTS_IN_P0) {
      dispatch(
        saveUserPreferencesAction({
          activationPreferences: {
            ...activationPreferences,
            setupGuideTasks: [
              ...new Set([
                ...(activationPreferences.setupGuideTasks ?? []),
                'knows-setup-guide-exists-in-p0',
              ]),
            ],
          },
        }),
      );
    }
  };

  return (
    <>
      <div ref={tipAnchor}>
        <SetupGuideTopBarButton
          className={props.className}
          onClick={() => {
            trackDataEvent(DataEventActionsEnum.CLICK, OnboardingDataEventObject.ADMIN_SETUP_GUIDE_OPEN);
            if (visibleContinueSetupTip) {
              openAdminSetupGuideSetupScreen();
            } else {
              openAdminSetupGuide();
            }
            onContinueSetupTipDismiss();
          }}
          taskProgress={tasks.setup.progress}
          tasksAreComplete={isCustomer}
        />
      </div>

      <StyledInfoBubble
        position="bottom"
        anchor={tipAnchor}
        onRequestClose={onContinueSetupTipDismiss}
        isVisible={!!visibleContinueSetupTip}
        isStandalone
      >
        <InfoBubbleContent>
          <InfoBubblePill type={InfoBubblePillVariantsEnum.EDUCATION} />
          <StyledInfoBubbleTitleDiv>
            <Trans id="admin-setup-guide-topbar-button-info-bubble-title">🚀 Continue setup</Trans>
          </StyledInfoBubbleTitleDiv>
          <InfoBubbleDescription>{renderContinueSetupTip(visibleContinueSetupTip)}</InfoBubbleDescription>
        </InfoBubbleContent>
        <StyledCloseButton onClick={onContinueSetupTipDismiss}>
          <StyledCloseIcon name="close" />
        </StyledCloseButton>
      </StyledInfoBubble>
      {renderOnboardingGuideTransition(OnboardingGuidesEnum.ADMIN_SETUP, (styles) => (
        <AdminSetupGuidePopover
          panelId={panelId}
          styles={styles}
          tasks={tasksSuspendedUntilOpen}
          defaultScreen={defaultScreen}
          onDismissPermanentlyClick={() => {
            closeAdminSetupGuide();
            setIsDismissPermanentlyDialogOpen(true);
          }}
          onMaybeShowContinueSetupTip={() => {
            if (!activationPreferences.setupGuideTasks?.includes('knows-setup-guide-exists-in-p0')) {
              requestContinueSetupTip(ContinueSetupTipsEnum.KNOWS_SETUP_GUIDE_EXISTS_IN_P0);
              setPendingContinueSetupTip(ContinueSetupTipsEnum.KNOWS_SETUP_GUIDE_EXISTS_IN_P0);
            }
          }}
          onClose={() => closeAdminSetupGuide()}
        />
      ))}
      {isDismissPermanentlyDialogOpen && (
        <DismissSetupGuideDialog
          onClose={() => {
            setIsDismissPermanentlyDialogOpen(false);
            openAdminSetupGuide();
          }}
          onOkClick={() => {
            trackDataEvent(
              DataEventActionsEnum.CLICK,
              OnboardingDataEventObject.ADMIN_SETUP_GUIDE_DISMISS_PERMANENTLY,
            );
            dispatch(
              saveUserPreferencesAction({
                activationPreferences: {
                  ...activationPreferences,
                  isSetupGuideDismissed: true,
                },
              }),
            );
            setIsDismissPermanentlyDialogOpen(false);
          }}
        />
      )}
    </>
  );
};

function renderContinueSetupTip(continueSetupTip: ContinueSetupTipsEnum | undefined): ReactNode {
  if (!continueSetupTip) {
    return null;
  }

  switch (continueSetupTip) {
    case ContinueSetupTipsEnum.CONNECT_CHANNEL:
      return (
        <Trans id="admin-setup-guide-topbar-button-info-bubble-connect-channel-description">
          Good job connecting a channel. Open the setup guide for next steps.
        </Trans>
      );

    case ContinueSetupTipsEnum.INVITE_TEAMMATES:
      return (
        <Trans id="admin-setup-guide-topbar-button-info-bubble-invite-teammates-description">
          Good job inviting teammates. Open the setup guide for next steps.
        </Trans>
      );

    case ContinueSetupTipsEnum.KNOWS_SETUP_GUIDE_EXISTS_IN_P0:
      return (
        <Trans id="admin-setup-guide-topbar-button-info-bubble-knows-guide-exists-in-p0-description">
          Our setup guide will make sure you’re successful during your first days in Front.
        </Trans>
      );

    default:
      assertUnreachable(continueSetupTip);
      return null;
  }
}

const getTask = (tasks: ReadonlyArray<SetupGuideScreenTask>, key: React.Key) =>
  tasks.find((task) => task.key === key);

// Show a tip "pulling" the user back into the setup guide at certain important cues.
const useContinueSetupTip = (
  previousTasks: AdminSetupGuideTasksAndProgress | null,
  tasks: AdminSetupGuideTasksAndProgress,
  isAdminSetupGuideOpen: boolean,
) => {
  const [pendingContinueSetupTip, setPendingContinueSetupTip] = useState<ContinueSetupTipsEnum | undefined>(
    undefined,
  );

  const previousSharedWorkspaceTask = getTask(previousTasks?.setup.tasks ?? [], sharedWorkspaceTask.key);
  const currentSharedWorkspaceTask = getTask(tasks.setup.tasks, sharedWorkspaceTask.key);
  const justCompletedSharedWorkspaceTask =
    previousSharedWorkspaceTask?.isComplete === false && currentSharedWorkspaceTask?.isComplete === true;

  const previousConnectChannelTask = getTask(previousTasks?.setup.tasks ?? [], connectChannelTask.key);
  const currentConnectChannelTask = getTask(tasks.setup.tasks, connectChannelTask.key);
  const justCompletedCurrentConnectChannelTask =
    previousConnectChannelTask?.isComplete === false && currentConnectChannelTask?.isComplete === true;

  const previousInviteTeammatesTask = getTask(previousTasks?.setup.tasks ?? [], inviteTeammatesTask.key);
  const currentInviteTeammatesTask = getTask(tasks.setup.tasks, inviteTeammatesTask.key);
  const justCompletedInviteTeammatesTask =
    previousInviteTeammatesTask?.isComplete === false && currentInviteTeammatesTask?.isComplete === true;

  useEffect(() => {
    if (isAdminSetupGuideOpen) {
      return;
    }

    if (justCompletedCurrentConnectChannelTask) {
      setPendingContinueSetupTip(ContinueSetupTipsEnum.CONNECT_CHANNEL);
    } else if (justCompletedInviteTeammatesTask) {
      setPendingContinueSetupTip(ContinueSetupTipsEnum.INVITE_TEAMMATES);
    }
  }, [
    isAdminSetupGuideOpen,
    setPendingContinueSetupTip,
    justCompletedSharedWorkspaceTask,
    justCompletedCurrentConnectChannelTask,
    justCompletedInviteTeammatesTask,
  ]);

  const {requestedContinueSetupTip, clearRequestedContinueSetupTip} = useOnboardingModalState();

  // We show the tip when (1) we saw the state transition and (2) it was requested by some other part of the UI.
  const visibleContinueSetupTip =
    pendingContinueSetupTip === requestedContinueSetupTip ? requestedContinueSetupTip : undefined;

  return {
    visibleContinueSetupTip,
    setPendingContinueSetupTip,
    clearRequestedContinueSetupTip,
  };
};

const computeDefaultScreen = (
  tasks: AdminSetupGuideTasksAndProgress,
  defaultAdminSetupGuideScreen: AdminSetupGuideScreensEnum,
) => {
  // If we just closed the resume setup guide tip, we always want to land on the setup tab.
  // If we're opening the setup guide from a sandbox demo, always land on the demos tab.
  if (defaultAdminSetupGuideScreen !== AdminSetupGuideScreensEnum.DEFAULT) {
    return defaultAdminSetupGuideScreen;
  }

  if (tasks.setup.progress.hasCompletedAllTasks) {
    return AdminSetupGuideScreensEnum.MORE;
  }

  return AdminSetupGuideScreensEnum.SETUP;
};
